import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, UserCheck, Users,
  FileText, LogOut, Bell
} from 'lucide-react';
import useAuthStore from '../../context/authStore';
import toast from 'react-hot-toast';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/referral-requests', icon: UserCheck, label: 'Referral Requests' },
  { to: '/directory', icon: Users, label: 'Member Directory' },
  { to: '/profile', icon: FileText, label: 'My Profile' },
];

export default function MemberLayout() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const initials = user
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase()
    : 'M';

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Sidebar */}
      <aside style={{
        width: 260, background: 'var(--primary)',
        display: 'flex', flexDirection: 'column',
        position: 'fixed', top: 0, bottom: 0, left: 0, zIndex: 100,
      }}>
        {/* Bug 3 & 7: BPS Branding with Logo */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 44, height: 44, borderRadius: '50%',
              background: 'white', overflow: 'hidden',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, border: '2px solid rgba(255,255,255,0.3)'
            }}>
              <img
                src="/logo.png"
                alt="BPS"
                style={{ width: '85%', height: '85%', objectFit: 'contain' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentNode.innerHTML = '<span style="color:#1a3a5c;font-weight:700;font-size:12px;">BPS</span>';
                }}
              />
            </div>
            <div>
              {/* Bug 3: Updated to Bombay Psychiatric Society */}
              <h1 style={{ color: 'white', fontSize: 13, fontFamily: "'Playfair Display', serif", lineHeight: 1.3 }}>
                Bombay Psychiatric Society
              </h1>
              <span style={{ color: 'var(--accent-light)', fontSize: 11 }}>Member Portal</span>
            </div>
          </div>
        </div>

        {/* User Info */}
        <div style={{ padding: '14px 24px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 38, height: 38, borderRadius: '50%',
              background: 'var(--accent)', color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 14
            }}>{initials}</div>
            <div>
              <div style={{ color: 'white', fontSize: 13, fontWeight: 600 }}>
                {user?.firstName} {user?.lastName}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>
                {user?.memberId || 'Registration Pending'}
              </div>
            </div>
          </div>
          {user?.memberStatus && (
            <div style={{ marginTop: 8 }}>
              <span className={`badge badge-${user.isActive ? 'active' : 'pending'}`} style={{ fontSize: 11 }}>
                {user.isActive ? '✓ Active Member' : `Status: ${user.memberStatus}`}
              </span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, padding: '12px 0', overflowY: 'auto' }}>
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '11px 24px',
                color: isActive ? 'white' : 'rgba(255,255,255,0.65)',
                background: isActive ? 'rgba(255,255,255,0.12)' : 'transparent',
                textDecoration: 'none', fontSize: 14,
                fontWeight: isActive ? 600 : 400,
                borderLeft: isActive ? '3px solid var(--accent)' : '3px solid transparent',
                transition: 'all 0.15s',
              })}
            >
              <Icon size={17} />{label}
            </NavLink>
          ))}
        </nav>

        {/* Logout */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <button onClick={handleLogout} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            color: 'rgba(255,255,255,0.7)', background: 'none',
            border: 'none', cursor: 'pointer', fontSize: 14, padding: '8px 0', width: '100%'
          }}>
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main style={{ flex: 1, marginLeft: 260, minHeight: '100vh' }}>
        <div style={{
          background: 'white', padding: '0 28px', height: 60,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, zIndex: 50
        }}>
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            Welcome back, <strong style={{ color: 'var(--primary)' }}>{user?.firstName}</strong>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Bell size={18} style={{ color: 'var(--text-muted)', cursor: 'pointer' }} />
            {!user?.hasProfile && (
              <NavLink to="/register-membership" className="btn btn-accent btn-sm">
                Complete Registration
              </NavLink>
            )}
          </div>
        </div>
        <div style={{ padding: '28px 32px' }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
